import React, { useContext, useEffect } from "react";
import { secured } from "react-abac";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, IntegrationTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import mixpanel from "mixpanel-browser";
import RestrictedSection from "~/components/RestrictedSection";
import { Alert, Box, Button, Dropdown, Grid, Icon, Modal, Section, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import CoreContext from "~/lib/context";
import { useDispatch, useSelector } from "react-redux";
import Markdown from "~/components/Markdown";
import StyledIcon from "~/components/StyledIcon";
import integrationSelectors from "~/store/integrations/selectors";
import InsightsWidgetsGrid from "~/components/InsightsWidgetsGrid";
import { DndProvider } from "react-dnd";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";
import useAbacExt from "~/hooks/abac";
import toastsActionCreators from "~/store/toasts/actions";
import { useQueryState } from "~/hooks/common";
import InputTopicSelect from "~/components/InputTopicSelect";
import insightsSelectors from "~/store/insights/selectors";
import { InsightsActionEnum } from "~/store/insights/types";
import InsightsReportCreateModal from "~/components/InsightsReportCreateModal";
import insightsActionCreators from "~/store/insights/actions";
import InsightsShareInSlackModal from "~/components/InsightsShareInSlackModal";
import InsightsConfigureViewDropdown from "~/components/InsightsConfigureViewDropdown";
import { useHistory } from "react-router-dom";
import topicSelectors from "~/store/topics/selectors";
import topicActionCreators from "~/store/topics/actions";
const LoadingSection = styled(Section)`
  height: 100vh;
  display: flex;
  align-items: center;
`;
const StyledGrid = styled(Grid)`
  position: sticky;
  top: 0;
  z-index: 2;
    background-color ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.secondary;
}};
`;
const InsightsExplorer = () => {
  const {
    t
  } = useTranslation();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const insights = useSelector(insightsSelectors.getInsights);
  const view = useSelector(insightsSelectors.getView);
  const insightsStatus = useSelector(insightsSelectors.getInsightsStatus);
  const insightsError = useSelector(insightsSelectors.getInsightsError);
  const sendStatus = useSelector(insightsSelectors.getSendStatus);
  const sendError = useSelector(insightsSelectors.getSendError);
  const filter = useSelector(insightsSelectors.getFilter);
  const action = useSelector(insightsSelectors.getAction);
  const dispatch = useDispatch();
  const followedStatus = useSelector(topicSelectors.getFollowedStatus);
  const followedTopics = useSelector(topicSelectors.getFollowedTopics);
  const followedError = useSelector(topicSelectors.getFollowedError);
  const error = insightsError || sendError || followedError;
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    userHas
  } = useAbacExt();
  const history = useHistory();
  useEffect(() => {
    if (followedStatus === StatusStateEnum.IDLE) {
      dispatch(topicActionCreators.fetchFollowedStarted());
    }
  }, [dispatch, followedStatus]);
  useQueryState({
    filter
  }, (_, value) => {
    dispatch(insightsActionCreators.filtered(value));
  });
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useEffect(() => {
    if (insightsStatus === StatusStateEnum.IDLE) {
      dispatch(insightsActionCreators.exploreStarted({
        filter
      }));
    }
  }, [insightsStatus]);
  const handleTopicsClick = () => {
    history.push(RoutesEnum.TOPICS);
  };
  if (followedStatus === StatusStateEnum.LOADING) {
    return /*#__PURE__*/React.createElement(LoadingSection, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  if (!followedTopics.length) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
      element: "h1",
      s: {
        mt: 5,
        mb: 8
      },
      className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
    }, t("InsightsExplorer.weeklyInsights")), error && /*#__PURE__*/React.createElement(Alert, {
      severity: "danger",
      s: {
        mb: 6
      },
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: error || "",
      linkColor: "text"
    })), /*#__PURE__*/React.createElement(Grid, {
      cols: 1,
      justify: "center",
      s: {
        mt: 16
      }
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "document",
      color: "translucentInfo"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 6
      },
      align: "center",
      style: {
        maxWidth: "400px"
      }
    }, t("InsightsExplorer.noFollowedTopics")), /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      fontSize: 4,
      display: "block",
      s: {
        mt: 3,
        mb: 5
      },
      lineHeight: 6,
      style: {
        maxWidth: "400px"
      },
      align: "center",
      text: t("InsightsExplorer.noFollowedTopicsInfo")
    }), /*#__PURE__*/React.createElement(Button, {
      color: "primary",
      onClick: handleTopicsClick
    }, t("InsightsExplorer.selectTopicsToFollow"))));
  }
  if (!insights && error) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
      element: "h1",
      s: {
        mt: 5,
        mb: 8
      },
      className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
    }, t("InsightsExplorer.weeklyIinsights")), error && /*#__PURE__*/React.createElement(Alert, {
      severity: "danger",
      s: {
        mb: 6
      },
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: error || "",
      linkColor: "text"
    })), /*#__PURE__*/React.createElement(Grid, {
      cols: 1,
      justify: "center",
      s: {
        mt: 16
      }
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "warningOutline",
      color: "translucentDanger"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 6
      },
      align: "center",
      style: {
        maxWidth: "400px"
      }
    }, t("InsightsExplorer.reportIssues")), /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      fontSize: 4,
      display: "block",
      s: {
        mt: 3,
        mb: 5
      },
      lineHeight: 6,
      style: {
        maxWidth: "400px"
      },
      align: "center",
      text: t("InsightsExplorer.reportIssuesInfo")
    })));
  }
  if (!insights) {
    return /*#__PURE__*/React.createElement(LoadingSection, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  const handleCreate = () => {
    mixpanel.track(`Insights – Schedule a Report`);
    dispatch(insightsActionCreators.actionChanged(CRUDActionEnum.CREATE, null));
  };
  const handleFilter = newFilter => {
    mixpanel.track(`Insights – Filter`, {
      filter: newFilter
    });
    dispatch(insightsActionCreators.filtered({
      ...filter,
      ...newFilter
    }));
  };
  const handleShareInSlack = () => {
    dispatch(insightsActionCreators.actionChanged(InsightsActionEnum.SHARE_IN_SLACK, null));
    mixpanel.track(`Insights – Share in Slack`);
  };
  const handleSendInSlack = callback => {
    dispatch(insightsActionCreators.sendSlackStarted({
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      topicIds: filter.topicIds
    }, _ref2 => {
      let {
        data
      } = _ref2;
      if (data) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("InsightsExplorer.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    mixpanel.track(`Insights – Send in Slack`);
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mt: 3,
      mb: 8
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("InsightsExplorer.weeklyInsights")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: error || "",
    linkColor: "text"
  })), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 2,
    gap: 3,
    template: "240px auto 1fr auto",
    s: {
      p: 3,
      mx: -3,
      mt: -3,
      mb: 0
    }
  }, /*#__PURE__*/React.createElement(InputTopicSelect, {
    placeholder: "All followed topics",
    value: filter.topicIds,
    onChange: topicIds => handleFilter({
      ...filter,
      topicIds
    }),
    filter: {
      followed: true
    },
    multiple: true
  }), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(DndProvider, {
    backend: HTML5Backend
  }, /*#__PURE__*/React.createElement(InsightsConfigureViewDropdown, null))), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(Box, {
    gap: 3
  }, userHas(AuthoritiesEnum.INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Button, {
    s: {
      py: 2.8,
      pl: 3,
      pr: 5
    },
    onClick: handleCreate,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "plus",
      s: {
        mr: 1
      }
    })
  }, t("InsightsExplorer.scheduleReport")), slackIntegration && /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2.8
      },
      color: "translucent",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      display: "block"
    }),
    fullWidth: true
  }, _ref3 => {
    let {
      isOpen,
      onItemClick
    } = _ref3;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, slackIntegration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleSendInSlack(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "send",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("InsightsExplorer.sendToSlack")), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "share",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("InsightsExplorer.shareInSlack"))));
  }))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(InsightsWidgetsGrid, {
    isLoading: insightsStatus === StatusStateEnum.LOADING,
    view: view,
    widgets: insights.widgets,
    dateFrom: insights.dateFrom,
    dateTo: insights.dateTo,
    topicIds: insights.topicIds,
    s: {
      mt: 2
    }
  })), /*#__PURE__*/React.createElement(Modal, {
    open: action === InsightsActionEnum.SHARE_IN_SLACK
  }, /*#__PURE__*/React.createElement(InsightsShareInSlackModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.CREATE
  }, /*#__PURE__*/React.createElement(InsightsReportCreateModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.INSIGHTS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(InsightsExplorer), () => true);